import { useRef, useState } from 'react';

import { GearbeltEvent } from '@gearbelt/global';
import { PhoneCheckContext } from '@site-mate/sitemate-global-shared/lib';

import { ApiService } from '@/services/api-v2';
import { signupOtpService } from '@/services/global-api/signup-otp.service';
import { SignUpSteps } from '@/stores/signup.store';

import { PhoneVerificationErrorReason, PhoneVerificationStatus } from './sign-up-phone-verification';
import { fetchV3TokenWithRetry, mapSendErrorReason, mapVerifyErrorReason, RECAPTCHA_FAILURE_MESSAGE, VALID_CAPTCHA_STATUS } from './signup-otp-utils';

interface UseSignupOtpFlowParams {
  getPhone: () => string;
  persistForm: () => void;
  onSubmit: () => Promise<void>;
  executeRecaptcha?: (action: string) => Promise<string>;
  trackEvent: (event: GearbeltEvent, properties?: Record<string, unknown>) => void;
  setSignUpStep: (step: SignUpSteps) => void;
  setLoading: (loading: boolean) => void;
  showErrorToast: (message: string) => void;
}

export function useSignupOtpFlow({
  getPhone,
  persistForm,
  onSubmit,
  executeRecaptcha,
  trackEvent,
  setSignUpStep,
  setLoading,
  showErrorToast,
}: UseSignupOtpFlowParams) {
  const otpTwoFactorIdRef = useRef<string | null>(null);
  const verificationTokenRef = useRef<string | null>(null);
  const [otpStatus, setOtpStatus] = useState<PhoneVerificationStatus>('entry');
  const [otpErrorReason, setOtpErrorReason] = useState<PhoneVerificationErrorReason | undefined>(undefined);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState(0);
  const otpDeliveryMethodRef = useRef<'sms' | 'call'>('sms');
  const otpProfileMatchCountRef = useRef<number>(0);

  async function onMobileNumberNext() {
    persistForm();

    setLoading(true);
    try {
      const phone = getPhone();
      const { clash, matchCount } = await ApiService.CheckPhoneOnSignup({ phone, context: PhoneCheckContext.Signup });

      if (!clash) {
        await onSubmit();
        return;
      }

      otpProfileMatchCountRef.current = matchCount;
      await sendOtpAndRoute('sms', true);
    } catch (error) {
      showErrorToast('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // reCAPTCHA failed: one toast, keep the user on their current step (no OTP sent).
  function failVerification() {
    trackEvent(GearbeltEvent.SignUpHumanVerificationRequired, {});
    showErrorToast(RECAPTCHA_FAILURE_MESSAGE);
    setOtpStatus('entry');
  }

  async function sendOtpAndRoute(deliveryMethod: 'sms' | 'call', trackSent = false) {
    otpDeliveryMethodRef.current = deliveryMethod;
    const phone = getPhone();

    let recaptchaToken = '';
    if (executeRecaptcha) {
      try {
        recaptchaToken = await fetchV3TokenWithRetry(executeRecaptcha, 'send_otp');
      } catch {
        failVerification();
        return;
      }
    }

    let sendResult: Awaited<ReturnType<typeof signupOtpService.send>>;
    try {
      sendResult = await signupOtpService.send(phone, recaptchaToken, deliveryMethod, PhoneCheckContext.Signup);
    } catch (error) {
      const { reason, cooldownSeconds } = mapSendErrorReason(error);
      setOtpErrorReason(reason);
      setOtpCooldownSeconds(cooldownSeconds);
      setOtpStatus('error');
      setSignUpStep(SignUpSteps.PhoneVerification);
      return;
    }

    if (sendResult.status !== VALID_CAPTCHA_STATUS || !sendResult.twoFactorId) {
      failVerification();
      return;
    }

    otpTwoFactorIdRef.current = sendResult.twoFactorId;
    // Only on the clash-triggered send, not on resend / request-a-call.
    if (trackSent) {
      trackEvent(GearbeltEvent.SignupOtpSent, { profileMatchCount: otpProfileMatchCountRef.current });
    }
    setOtpErrorReason(undefined);
    setOtpCooldownSeconds(0);
    setOtpStatus('entry');
    setSignUpStep(SignUpSteps.PhoneVerification);
  }

  async function onOtpVerify(code: string) {
    if (!otpTwoFactorIdRef.current) {
      setOtpErrorReason('expired');
      setOtpStatus('error');
      return;
    }

    setOtpErrorReason(undefined);
    setOtpStatus('submitting');
    try {
      const { verificationToken: token } = await signupOtpService.verify(otpTwoFactorIdRef.current, code);
      verificationTokenRef.current = token;
      trackEvent(GearbeltEvent.SignupOtpVerified);
    } catch (error) {
      const failureReason = mapVerifyErrorReason(error);
      trackEvent(GearbeltEvent.SignupOtpFailed, { failureReason });
      setOtpErrorReason(failureReason);
      setOtpStatus('error');
      return;
    }

    // onSubmit navigates away on success; on any return-or-throw it leaves us here, so always clear the spinner.
    try {
      await onSubmit();
    } finally {
      setOtpStatus('entry');
    }
  }

  async function onOtpResend() {
    await sendOtpAndRoute('sms');
  }

  async function onOtpRequestCall() {
    await sendOtpAndRoute('call');
  }

  function resetForNewNumber() {
    otpTwoFactorIdRef.current = null;
    // Drop the previous number's token so it can't be submitted against a different phone.
    verificationTokenRef.current = null;
    setOtpErrorReason(undefined);
    setOtpStatus('entry');
    setSignUpStep(SignUpSteps.MobileNumber);
  }

  return {
    otpStatus,
    otpErrorReason,
    otpCooldownSeconds,
    verificationTokenRef,
    otpDeliveryMethodRef,
    onMobileNumberNext,
    sendOtpAndRoute,
    onOtpVerify,
    onOtpResend,
    onOtpRequestCall,
    resetForNewNumber,
  };
}
